import React, { useCallback, useRef, useState } from 'react';
import classnames from 'classnames';
import { useInitialIntersection } from '../../hooks';
import styles from './dynamic-media-picture.module.scss';
import { generateSrcSet } from './generate-src-set';
import { generateSrc } from './generate-src';
import { AspectRatioType, aspectRatioMapping } from '../../utils';

export type DynamicMediaImageConfigs = SingleDynamicMediaImageConfigs | MultiDynamicMediaImageConfigs;

export type SingleDynamicMediaImageConfigs = [Omit<DynamicMediaImageConfig, 'media'>];
export type MultiDynamicMediaImageConfigs = [
  DynamicMediaImageConfig,
  DynamicMediaImageConfig,
  ...DynamicMediaImageConfig[]
];

export type DynamicMediaPictureProps = {
  imageUrl: string;
  imageConfigs: DynamicMediaImageConfigs;
  imageAltText: string;
  className?: string;
  initialIsInView?: boolean;
  noLoadingLogo?: boolean;
  isObjectFit?: boolean;
  fetchPriority?: string;
  hasDarkeningLayer?: boolean;
  hasImageTransparency?: boolean;
  additionalUrlParameters?: Record<string, string>;
  omitAspectRatioInUrl?: boolean;
  maxImageHeight?: number;
};

export type DynamicMediaImageConfig = {
  aspectRatio: AspectRatioType;
  sizes: string;
  media: string;
};

const isSingleImageConfigs = (imageConfigs: DynamicMediaImageConfigs): imageConfigs is SingleDynamicMediaImageConfigs =>
  imageConfigs.length === 1;

const baseClass = 'dynamic-media-picture';
const base64Pixel = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const minWidth = 320 * 2;
const interval = 100 * 2;

export const DynamicMediaPicture: React.FC<DynamicMediaPictureProps> = ({
  imageUrl,
  imageConfigs,
  imageAltText,
  className,
  initialIsInView = false,
  noLoadingLogo,
  isObjectFit,
  fetchPriority,
  hasDarkeningLayer,
  hasImageTransparency,
  additionalUrlParameters,
  omitAspectRatioInUrl,
  maxImageHeight
}) => {
  const maxHeight = maxImageHeight && maxImageHeight >= 640 ? maxImageHeight : 2160;
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(initialIsInView);
  const [isLoaded, setIsLoaded] = useState(false);

  const handleLoad = () => isInView && setIsLoaded(true);
  const handleIntersection = useCallback(() => setIsInView(true), []);

  useInitialIntersection(ref, handleIntersection, `100%`);

  const classes = classnames(
    styles[baseClass],
    {
      [styles.transparent]: hasImageTransparency,
      [styles.loading]: !isLoaded && isInView,
      [styles.noLogo]: noLoadingLogo,
      [styles.objectFit]: isObjectFit
    },
    className
  );

  if (isSingleImageConfigs(imageConfigs)) {
    const [imageConfig] = imageConfigs;

    return (
      <img
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line react/no-unknown-property
        fetchpriority={fetchPriority}
        ref={ref}
        className={classes}
        src={
          isInView
            ? generateSrc({
                url: imageUrl,
                height: maxHeight,
                width: maxHeight / aspectRatioMapping[imageConfig.aspectRatio],
                aspectRatio: imageConfig.aspectRatio,
                hasDarkeningLayer,
                hasImageTransparency,
                additionalUrlParameters,
                omitAspectRatioInUrl
              })
            : base64Pixel
        }
        alt={imageAltText}
        onLoad={handleLoad}
        srcSet={
          isInView
            ? generateSrcSet({
                maxHeight,
                minWidth,
                interval,
                aspectRatio: imageConfig.aspectRatio,
                url: imageUrl,
                hasDarkeningLayer,
                hasImageTransparency,
                additionalUrlParameters,
                omitAspectRatioInUrl
              })
            : base64Pixel
        }
        data-testid="dynamicmediaimage-component"
      />
    );
  } else
    return (
      <picture ref={ref} className={classes} data-testid="dynamicmediapicture-component">
        {imageConfigs.map((config) => (
          <source
            key={config.media}
            media={config.media}
            srcSet={
              isInView
                ? generateSrcSet({
                    maxHeight,
                    minWidth,
                    interval,
                    aspectRatio: config.aspectRatio,
                    url: imageUrl,
                    hasDarkeningLayer,
                    hasImageTransparency,
                    additionalUrlParameters
                  })
                : base64Pixel
            }
            sizes={config.sizes}
          />
        ))}
        <img
          className={styles.image}
          src={base64Pixel}
          alt={imageAltText}
          onLoad={handleLoad}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          // eslint-disable-next-line react/no-unknown-property
          fetchpriority={fetchPriority}
        />
      </picture>
    );
};
